import React, { useEffect, useMemo, useState } from "react";
import { Download, Users } from "lucide-react";

import { getAllStudents } from "../../services/StudentRecordService";

import * as AttendanceRecordService from "../../services/AttendanceRecordService";

const {
  listAttendanceTimesheets,
} = AttendanceRecordService;

const WEEK_NUMBERS = Array.from(
  { length: 10 },
  (_, index) => index + 1
);

const CAMPUS_OPTIONS = [
  "Melbourne",
  "Canberra",
];

const EMPTY_STUDENT_RECORDS = [];

const STANDARD_HOURS_PER_WEEK = 20;
const VOE_HOURS_PER_WEEK = 32;

const ATTENDANCE_CATEGORIES = [
  {
    id: "zero",
    label: "0%",
    description: "Exactly 0%",
  },
  {
    id: "under50",
    label: "Less than 50%",
    description: "More than 0% and below 50%",
  },
  {
    id: "50to80",
    label: "50–80%",
    description: "50% to below 80%",
  },
  {
    id: "80to100",
    label: "80–100%",
    description: "80% to 100%",
  },
];

const CATEGORY_ORDER = {
  zero: 0,
  under50: 1,
  "50to80": 2,
  "80to100": 3,
};

function hasEnteredValue(value) {
  return value !== "" && value != null;
}

function getWeek(student, weekNumber) {
  return (
    student?.weeks?.[`week${weekNumber}`] || {
      attendanceHours: "",
      leaveApplied: false,
      leaveHours: "",
    }
  );
}

function weekHasEntry(week = {}) {
  return (
    Boolean(week.leaveApplied) ||
    hasEnteredValue(week.attendanceHours)
  );
}

function getHoursPerWeek(student = {}) {
  const enrolmentType = String(
    student.enrolmentType || ""
  )
    .trim()
    .toUpperCase();
  const attendanceDaysPerWeek = Number(
    student.attendanceDaysPerWeek
  );

  return attendanceDaysPerWeek === 5
    ? VOE_HOURS_PER_WEEK
    : STANDARD_HOURS_PER_WEEK;
}

function clampHours(value, maximumHours) {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return 0;
  }

  return Math.min(
    Math.max(parsed, 0),
    maximumHours
  );
}

function getCreditedHours(
  week = {},
  hoursPerWeek
) {
  const attendanceHours = clampHours(
    week.attendanceHours,
    hoursPerWeek
  );

  const approvedLeaveHours =
    week.leaveApplied
      ? week.leaveHours === "" ||
        week.leaveHours == null
        ? hoursPerWeek 
        : clampHours(
            week.leaveHours,
            hoursPerWeek
          )
      : 0;

  /*
   * Approved leave hours are added to attendance hours.
   * The credited total cannot exceed the student's
   * weekly maximum: 20 hours for COE or 32 hours for VOE.
   */
  return Math.min(
    attendanceHours + approvedLeaveHours,
    hoursPerWeek
  );
}

function calculateAttendanceForPeriod(
  student = {},
  selectedWeekNumber
) {
  const hoursPerWeek =
    getHoursPerWeek(student);

  const selectedWeek = getWeek(
    student,
    selectedWeekNumber
  );

  const selectedWeekPercentage =
    weekHasEntry(selectedWeek)
      ? Math.round(
          (
            getCreditedHours(
              selectedWeek,
              hoursPerWeek
            ) /
            hoursPerWeek
          ) * 100
        )
      : null;

  let cumulativeCreditedHours = 0;

  for (
    let weekNumber = 1;
    weekNumber <= selectedWeekNumber;
    weekNumber += 1
  ) {
    const week = getWeek(
      student,
      weekNumber
    );

    if (!weekHasEntry(week)) {
      continue;
    }

    cumulativeCreditedHours +=
      getCreditedHours(
        week,
        hoursPerWeek
      );
  }

  /*
   * Up-to-date attendance uses every scheduled week
   * from Week 1 through the selected week.
   * Missing weeks therefore contribute zero hours.
   */
  const cumulativePossibleHours =
    selectedWeekNumber * hoursPerWeek;

  const upToDatePercentage =
    cumulativePossibleHours > 0
      ? Math.round(
          (
            cumulativeCreditedHours /
            cumulativePossibleHours
          ) * 100
        )
      : 0;

  return {
    hoursPerWeek,
    selectedWeekPercentage,
    cumulativeCreditedHours,
    cumulativePossibleHours,
    upToDatePercentage,
  };
}

function getAttendanceCategory(percentage) {
  if (percentage === 0) {
    return "zero";
  }

  if (percentage < 50) {
    return "under50";
  }

  if (percentage < 80) {
    return "50to80";
  }

  return "80to100";
}

function getCategoryLabel(categoryId) {
  return (
    ATTENDANCE_CATEGORIES.find(
      (category) =>
        category.id === categoryId
    )?.label || "—"
  );
}

function escapeCsvValue(value) {
  const text = String(value ?? "");

  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
}

function normaliseFileNamePart(value) {
  return String(value || "")
    .trim()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "") || "attendance";
}

function downloadCsvFile(fileName, rows) {
  const csv = rows
    .map((row) =>
      row.map(escapeCsvValue).join(",")
    )
    .join("\r\n");

  const blob = new Blob(
    ["\uFEFF", csv],
    {
      type: "text/csv;charset=utf-8;",
    }
  );

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}


function reminderIsChecked(value) {
  if (
    value &&
    typeof value === "object"
  ) {
    return reminderIsChecked(
      value.checked ??
      value.sent ??
      value.value
    );
  }

  if (value === true) {
    return true;
  }

  return [
    "Y",
    "YES",
    "TRUE",
    "1",
  ].includes(
    String(value || "")
      .trim()
      .toUpperCase()
  );
}

function normaliseDailyReminders(
  value = {}
) {
  return Object.fromEntries(
    Array.from(
      { length: 5 },
      (_, index) => {
        const dayKey =
          `day${index + 1}`;

        return [
          dayKey,
          reminderIsChecked(
            value?.[dayKey]
          ),
        ];
      }
    )
  );
}

function normaliseCourseKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function parseAttendanceDaysPerWeek(
  value,
  fallback = null
) {
  if (value === 3 || value === 5) {
    return value;
  }

  if (Array.isArray(value)) {
    const count = value.filter(Boolean).length;

    if (count === 3 || count === 5) {
      return count;
    }
  }

  if (
    value &&
    typeof value === "object"
  ) {
    const nestedValue =
      value.classPerWeek ??
      value.attendanceDaysPerWeek ??
      value.daysPerWeek ??
      value.numberOfDays ??
      value.days;

    if (
      nestedValue !== undefined &&
      nestedValue !== value
    ) {
      const nestedResult =
        parseAttendanceDaysPerWeek(
          nestedValue,
          null
        );

      if (nestedResult) {
        return nestedResult;
      }
    }

    const weekdayKeys = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
    ];

    const selectedWeekdays =
      weekdayKeys.filter((day) =>
        Boolean(
          value[day] ??
          value[
            day.charAt(0).toUpperCase() +
            day.slice(1)
          ]
        )
      ).length;

    if (
      selectedWeekdays === 3 ||
      selectedWeekdays === 5
    ) {
      return selectedWeekdays;
    }
  }

  const match = String(value ?? "")
    .trim()
    .match(/\b(3|5)\s*(?:day|days)?\b/i);

  if (match) {
    return Number(match[1]);
  }

  return fallback;
}

function buildCourseScheduleMap(
  form = {},
  record = {}
) {
  const scheduleMap = {};

  const courses = [
    ...(Array.isArray(form.courses)
      ? form.courses
      : []),
    ...(Array.isArray(record.courses)
      ? record.courses
      : []),
  ];

  courses.forEach((course) => {
    /*
     * StudentRecordForm saves the schedule on each
     * course as course.classPerWeek: "3 days" or "5 days".
     */
    const attendanceDaysPerWeek =
      parseAttendanceDaysPerWeek(
        course?.classPerWeek ??
        course?.attendanceDaysPerWeek ??
        course?.daysPerWeek,
        3
      );

    [
      course?.courseName,
      course?.courseCode,
      course?.code,
    ]
      .map(normaliseCourseKey)
      .filter(Boolean)
      .forEach((key) => {
        scheduleMap[key] =
          attendanceDaysPerWeek;
      });
  });

  return scheduleMap;
}

function findCourseAttendanceDays(
  courseSchedules = {},
  courseName,
  courseCode,
  savedStudent = {}
) {
  const wantedKeys = [
    normaliseCourseKey(courseName),
    normaliseCourseKey(courseCode),
  ].filter(Boolean);

  for (const key of wantedKeys) {
    if (courseSchedules[key]) {
      return courseSchedules[key];
    }
  }

  /*
   * Handles small differences in saved course labels,
   * such as an added course code or punctuation.
   */
  const availableKeys =
    Object.keys(courseSchedules);

  for (const wantedKey of wantedKeys) {
    const matchingKey =
      availableKeys.find(
        (availableKey) =>
          availableKey.includes(wantedKey) ||
          wantedKey.includes(availableKey)
      );

    if (matchingKey) {
      return courseSchedules[matchingKey];
    }
  }

  return parseAttendanceDaysPerWeek(
    savedStudent.classPerWeek ??
    savedStudent.attendanceDaysPerWeek,
    3
  );
}


function getReminderValues(
  week = {}
) {
  return {
    dailyReminders:
      normaliseDailyReminders(
        week.dailyReminders ??
        week.dailyAttendanceReminders
      ),

    weeklyReminderSent:
      reminderIsChecked(
        week.weeklyReminderSent ??
        week.weeklyAttendanceReminderSent
      ),

    studentResponse: String(
      week.studentResponse ??
      week.responseFromStudent ??
      ""
    ),
  };
}

function getReminderRowKey(
  student,
  weekNumber
) {
  return [
    student.timesheetId,
    student.studentKey,
    `week${weekNumber}`,
  ].join("::");
}

function buildStudentDetailsMap(
  studentRecords = []
) {
  const details = new Map();

  studentRecords.forEach((record) => {
    const form =
      record?.formState || {};

    const studentId = String(
      form.studentId ||
      record.studentId ||
      ""
    ).trim();

    if (!studentId) {
      return;
    }

    const rawEnrolmentType = String(
      form.enrolmentType ||
      record.enrolmentType ||
      ""
    )
      .trim()
      .toUpperCase();

    details.set(studentId, {
      contactEmail: String(
        form.email ||
        form.contactEmail ||
        record.contactEmail ||
        ""
      ).trim(),

      contactNumber: String(
        form.phone ||
        form.contactNumber ||
        record.contactNumber ||
        ""
      ).trim(),

      enrolmentType:
        rawEnrolmentType === "VOE"
          ? "VOE"
          : "COE",

      /*
       * Schedule is course-specific, not student-wide.
       * Each form.courses item stores classPerWeek.
       */
      courseSchedules:
        buildCourseScheduleMap(
          form,
          record
        ),
    });
  });

  return details;
}


export default function AttendanceRecordBrowser({
  studentRecords: suppliedStudentRecords =
    EMPTY_STUDENT_RECORDS,
}) {
  const [allTimesheets, setAllTimesheets] =
    useState([]);

  const [
    currentStudentDetailsById,
    setCurrentStudentDetailsById,
  ] = useState(new Map());

  const [loading, setLoading] =
    useState(true);

  const [
    campusFilter,
    setCampusFilter,
  ] = useState("Melbourne");

  const [year, setYear] =
    useState("");

  const [term, setTerm] =
    useState("");

  const [
    selectedWeek,
    setSelectedWeek,
  ] = useState("");

  const [
    enrolmentTypeFilter,
    setEnrolmentTypeFilter,
  ] = useState("COE");

  const [showResults, setShowResults] =
    useState(false);

  const [
    selectedCategory,
    setSelectedCategory,
  ] = useState("all");

  const [error, setError] =
    useState("");

  const [savingRows, setSavingRows] =
    useState({});

  const [savedRows, setSavedRows] =
    useState({});

  const loadAll = async () => {
    setLoading(true);
    setError("");

    try {
      const timesheetsPromise =
        listAttendanceTimesheets();

      const studentsPromise =
        Array.isArray(
          suppliedStudentRecords
        ) &&
        suppliedStudentRecords.length > 0
          ? Promise.resolve(
              suppliedStudentRecords
            )
          : getAllStudents();

      const [
        timesheets,
        loadedStudentRecords,
      ] = await Promise.all([
        timesheetsPromise,
        studentsPromise,
      ]);

      setAllTimesheets(
        Array.isArray(timesheets)
          ? timesheets
          : []
      );

      setCurrentStudentDetailsById(
        buildStudentDetailsMap(
          Array.isArray(
            loadedStudentRecords
          )
            ? loadedStudentRecords
            : []
        )
      );
    } catch (loadError) {
      console.error(loadError);

      setError(
        "Saved attendance records or student contact details could not be loaded."
      );

      setAllTimesheets([]);
      setCurrentStudentDetailsById(
        new Map()
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [suppliedStudentRecords]);

  /*
   * Once all three filters have values, refresh the audit
   * automatically whenever Year, Term or Attendance Period changes.
   * The View Attendance button still works for the initial/manual view,
   * but repeated clicks are no longer required.
   */
  useEffect(() => {
    const hasCompleteFilters = Boolean(
      year &&
      term &&
      selectedWeek
    );

    setShowResults(hasCompleteFilters);
    setSelectedCategory("all");
  }, [
    campusFilter,
    year,
    term,
    selectedWeek,
  ]);

  const yearOptions = useMemo(
    () =>
      [
        ...new Set(
          allTimesheets
            .filter(
              (timesheet) =>
                String(
                  timesheet.campus || ""
                )
                  .trim()
                  .toLowerCase() ===
                campusFilter.toLowerCase()
            )
            .map((timesheet) =>
              Number(timesheet.year)
            )
            .filter(Number.isFinite)
        ),
      ].sort((a, b) => b - a),
    [
      allTimesheets,
      campusFilter,
    ]
  );

  const termOptions = useMemo(() => {
    if (!year) {
      return [];
    }

    return [
      ...new Set(
        allTimesheets
          .filter(
            (timesheet) =>
              String(
                timesheet.campus || ""
              )
                .trim()
                .toLowerCase() ===
                campusFilter.toLowerCase() &&
              Number(timesheet.year) ===
                Number(year)
          )
          .map(
            (timesheet) =>
              timesheet.term
          )
          .filter(Boolean)
      ),
    ].sort((a, b) =>
      String(a).localeCompare(
        String(b),
        undefined,
        {
          numeric: true,
        }
      )
    );
  }, [
    allTimesheets,
    campusFilter,
    year,
  ]);

  const matchingTimesheets = useMemo(() => {
    if (
      !showResults ||
      !year ||
      !term ||
      !selectedWeek
    ) {
      return [];
    }

    return allTimesheets.filter(
      (timesheet) =>
        String(
          timesheet.campus || ""
        )
          .trim()
          .toLowerCase() ===
          campusFilter.toLowerCase() &&
        Number(timesheet.year) ===
          Number(year) &&
        timesheet.term === term
    );
  }, [
    allTimesheets,
    campusFilter,
    year,
    term,
    selectedWeek,
    showResults,
  ]);

  const studentRows = useMemo(() => {
    const selectedWeekNumber =
      Number(selectedWeek);

    if (
      !Number.isInteger(
        selectedWeekNumber
      ) ||
      selectedWeekNumber < 1 ||
      selectedWeekNumber > 10
    ) {
      return [];
    }

    return matchingTimesheets
      .flatMap((timesheet) =>
        Object.entries(
          timesheet.students || {}
        ).map(([
          studentKey,
          savedStudent,
        ]) => {
          const studentId = String(
            savedStudent.studentId || ""
          ).trim();

          const currentDetails =
            currentStudentDetailsById.get(
              studentId
            ) || {};

          /*
           * Current student records take priority for
           * contact details and VOE classification.
           */
          const attendanceDaysPerWeek =
            findCourseAttendanceDays(
              currentDetails.courseSchedules ||
                {},
              timesheet.courseName,
              timesheet.courseCode,
              savedStudent
            );

          const student = {
            ...savedStudent,

            contactEmail:
              currentDetails.contactEmail ||
              savedStudent.contactEmail ||
              "",

            contactNumber:
              currentDetails.contactNumber ||
              savedStudent.contactNumber ||
              "",

            enrolmentType:
              currentDetails.enrolmentType ||
              (
                String(
                  savedStudent.enrolmentType ||
                  ""
                )
                  .trim()
                  .toUpperCase() === "VOE"
                  ? "VOE"
                  : "COE"
              ),

            attendanceDaysPerWeek,
          };


          const attendance =
            calculateAttendanceForPeriod(
              student,
              selectedWeekNumber
            );

          const attendanceCategory =
            attendance.selectedWeekPercentage == null
              ? null
              : getAttendanceCategory(
                  attendance.selectedWeekPercentage
                );

          const selectedWeekRecord =
            getWeek(
              student,
              selectedWeekNumber
            );

          const reminderValues =
            getReminderValues(
              selectedWeekRecord
            );

          return {
            ...student,
            ...attendance,
            ...reminderValues,
            attendanceCategory,
            studentKey,

            courseCode:
              timesheet.courseCode || "",

            courseName:
              timesheet.courseName ||
              "Unknown Course",

            timesheetId:
              timesheet.id,
          };
        })
      )
      .sort((a, b) => {
        const categoryDifference =
          CATEGORY_ORDER[
            a.attendanceCategory
          ] -
          CATEGORY_ORDER[
            b.attendanceCategory
          ];

        if (categoryDifference !== 0) {
          return categoryDifference;
        }

        const percentageDifference =
          a.upToDatePercentage -
          b.upToDatePercentage;

        if (percentageDifference !== 0) {
          return percentageDifference;
        }

        const courseDifference =
          String(
            a.courseName || ""
          ).localeCompare(
            String(
              b.courseName || ""
            )
          );

        if (courseDifference !== 0) {
          return courseDifference;
        }

        return String(
          a.studentName || ""
        ).localeCompare(
          String(
            b.studentName || ""
          )
        );
      });
  }, [
    matchingTimesheets,
    selectedWeek,
    currentStudentDetailsById,
  ]);

  const enrolmentFilteredStudentRows =
    useMemo(() => {
      return studentRows.filter(
        (student) =>
          String(
            student.enrolmentType || "COE"
          )
            .trim()
            .toUpperCase() ===
          enrolmentTypeFilter
      );
    }, [
      studentRows,
      enrolmentTypeFilter,
    ]);

  const categoryCounts = useMemo(() => {
    const counts = {
      zero: 0,
      under50: 0,
      "50to80": 0,
      "80to100": 0,
    };

    enrolmentFilteredStudentRows.forEach(
      (student) => {
        if (!student.attendanceCategory) {
          return;
        }

        counts[
          student.attendanceCategory
        ] += 1;
      }
    );

    return counts;
  }, [enrolmentFilteredStudentRows]);

  const filteredStudentRows =
    useMemo(() => {
      if (selectedCategory === "all") {
        return enrolmentFilteredStudentRows;
      }

      return enrolmentFilteredStudentRows.filter(
        (student) =>
          student.attendanceCategory ===
          selectedCategory
      );
    }, [
      enrolmentFilteredStudentRows,
      selectedCategory,
    ]);

  const selectedWeekNumber =
    Number(selectedWeek);

  const downloadCategoryAttendance = (
    category
  ) => {
    const categoryRows =
      enrolmentFilteredStudentRows.filter(
        (student) =>
          student.attendanceCategory ===
          category.id
      );

    if (categoryRows.length === 0) {
      return;
    }

    const headers = [
      "Student ID",
      "Student Name",
      "Enrolment Type",
      "Course Code",
      "Course Name",
      "Contact Email",
      "Contact Number",
      "Attendance Period",
      `Week ${selectedWeekNumber} Attendance %`,
      `Up to Week ${selectedWeekNumber} Attendance %`,
      "Attendance Category",
      "Class Days Per Week",
      "Day 1 Reminder Sent",
      "Day 2 Reminder Sent",
      "Day 3 Reminder Sent",
      "Day 4 Reminder Sent",
      "Day 5 Reminder Sent",
      "Weekly Reminder Sent",
      "Student Response",
    ];

    const dataRows = categoryRows.map(
      (student) => {
        const attendanceDaysPerWeek =
          Number(
            student.attendanceDaysPerWeek ||
            3
          );

        const dailyReminderValue = (
          dayNumber
        ) => {
          if (
            dayNumber >
            attendanceDaysPerWeek
          ) {
            return "N/A";
          }

          return student.dailyReminders?.[
            `day${dayNumber}`
          ]
            ? "Yes"
            : "No";
        };

        return [
          student.studentId || "",
          student.studentName || "",
          student.enrolmentType || "COE",
          student.courseCode || "",
          student.courseName || "",
          student.contactEmail || "",
          student.contactNumber || "",
          `Week ${selectedWeekNumber}`,
          student.selectedWeekPercentage ==
          null
            ? ""
            : student.selectedWeekPercentage,
          student.upToDatePercentage,
          category.label,
          attendanceDaysPerWeek,
          dailyReminderValue(1),
          dailyReminderValue(2),
          dailyReminderValue(3),
          dailyReminderValue(4),
          dailyReminderValue(5),
          student.weeklyReminderSent
            ? "Yes"
            : "No",
          student.studentResponse || "",
        ];
      }
    );

    const fileName = [
      "Student-Attendance",
      normaliseFileNamePart(
        campusFilter
      ),
      normaliseFileNamePart(
        enrolmentTypeFilter
      ),
      normaliseFileNamePart(year),
      normaliseFileNamePart(term),
      `Week-${selectedWeekNumber}`,
      normaliseFileNamePart(
        category.label
      ),
    ].join("-") + "%.csv";

    downloadCsvFile(fileName, [
      headers,
      ...dataRows,
    ]);
  };

  const updateLocalReminderWeek = (
    student,
    nextReminderValues
  ) => {
    const weekNumber =
      Number(selectedWeek);

    if (
      !Number.isInteger(weekNumber)
    ) {
      return;
    }

    const weekKey =
      `week${weekNumber}`;

    setAllTimesheets(
      (currentTimesheets) =>
        currentTimesheets.map(
          (timesheet) => {
            if (
              timesheet.id !==
              student.timesheetId
            ) {
              return timesheet;
            }

            const timesheetStudents = {
              ...(timesheet.students || {}),
            };

            const savedStudent = {
              ...(timesheetStudents[
                student.studentKey
              ] || {}),
            };

            const weeks = {
              ...(savedStudent.weeks || {}),
            };

            weeks[weekKey] = {
              ...(weeks[weekKey] || {}),

              dailyReminders:
                normaliseDailyReminders(
                  nextReminderValues
                    .dailyReminders
                ),

              weeklyReminderSent:
                Boolean(
                  nextReminderValues
                    .weeklyReminderSent
                ),

              studentResponse: String(
                nextReminderValues
                  .studentResponse || ""
              ),
            };

            timesheetStudents[
              student.studentKey
            ] = {
              ...savedStudent,
              attendanceDaysPerWeek:
                student.attendanceDaysPerWeek,
              weeks,
            };

            return {
              ...timesheet,
              students: timesheetStudents,
            };
          }
        )
    );
  };

  const saveReminderValues = async (
    student,
    nextReminderValues
  ) => {
    const weekNumber =
      Number(selectedWeek);

    if (
      !Number.isInteger(weekNumber) ||
      !student.timesheetId ||
      !student.studentKey
    ) {
      return;
    }

    const rowKey = getReminderRowKey(
      student,
      weekNumber
    );

    setSavingRows(
      (current) => ({
        ...current,
        [rowKey]: true,
      })
    );

    setSavedRows(
      (current) => ({
        ...current,
        [rowKey]: false,
      })
    );

    setError("");

    try {
      if (
        typeof AttendanceRecordService
          .updateAttendanceStudentFollowUp !==
        "function"
      ) {
        throw new Error(
          "Add updateAttendanceStudentFollowUp to AttendanceRecordService before saving reminders."
        );
      }

      await AttendanceRecordService
        .updateAttendanceStudentFollowUp({
        timesheetId:
          student.timesheetId,
        studentKey:
          student.studentKey,
        weekNumber,
        attendanceDaysPerWeek:
          student.attendanceDaysPerWeek,
        dailyReminders:
          normaliseDailyReminders(
            nextReminderValues
              .dailyReminders
          ),
        weeklyReminderSent:
          Boolean(
            nextReminderValues
              .weeklyReminderSent
          ),
        studentResponse: String(
          nextReminderValues
            .studentResponse || ""
        ).trim(),
      });

      setSavedRows(
        (current) => ({
          ...current,
          [rowKey]: true,
        })
      );
    } catch (saveError) {
      console.error(saveError);

      setError(
        saveError?.message ||
        "The reminder information could not be saved."
      );
    } finally {
      setSavingRows(
        (current) => ({
          ...current,
          [rowKey]: false,
        })
      );
    }
  };

  const changeDailyReminder = (
    student,
    dayKey,
    checked
  ) => {
    const nextReminderValues = {
      dailyReminders: {
        ...student.dailyReminders,
        [dayKey]: checked,
      },
      weeklyReminderSent:
        student.weeklyReminderSent,
      studentResponse:
        student.studentResponse,
    };

    updateLocalReminderWeek(
      student,
      nextReminderValues
    );

    saveReminderValues(
      student,
      nextReminderValues
    );
  };

  const changeWeeklyReminder = (
    student,
    checked
  ) => {
    const nextReminderValues = {
      dailyReminders:
        student.dailyReminders,
      weeklyReminderSent: checked,
      studentResponse:
        student.studentResponse,
    };

    updateLocalReminderWeek(
      student,
      nextReminderValues
    );

    saveReminderValues(
      student,
      nextReminderValues
    );
  };

  const changeStudentResponse = (
    student,
    value
  ) => {
    updateLocalReminderWeek(
      student,
      {
        dailyReminders:
          student.dailyReminders,
        weeklyReminderSent:
          student.weeklyReminderSent,
        studentResponse: value,
      }
    );
  };

  const saveStudentResponse = (
    student
  ) => {
    saveReminderValues(
      student,
      {
        dailyReminders:
          student.dailyReminders,
        weeklyReminderSent:
          student.weeklyReminderSent,
        studentResponse:
          student.studentResponse,
      }
    );
  };

  return (
    <div>
      <div style={headingRowStyle}>
        <div>
          <div style={headingStyle}>

            <h3 style={titleStyle}>
              Records Audit & Reminders
            </h3>
          </div>
        </div>
      </div>

      <div style={panelStyle}>
        <div style={filterGridStyle}>
          <Field label="Campus">
            <select
              value={campusFilter}
              onChange={(event) => {
                setCampusFilter(
                  event.target.value
                );

                setYear("");
                setTerm("");
                setSelectedWeek("");
                setSelectedCategory("all");
              }}
              style={fieldStyle}
              disabled={loading}
            >
              {CAMPUS_OPTIONS.map(
                (campus) => (
                  <option
                    key={campus}
                    value={campus}
                  >
                    {campus}
                  </option>
                )
              )}
            </select>
          </Field>

          <Field label="Year">
            <select
              value={year}
              onChange={(event) => {
                setYear(
                  event.target.value
                );

                setTerm("");
              }}
              style={fieldStyle}
              disabled={loading}
            >
              <option value="">
                {loading
                  ? "Loading years…"
                  : "Select year"}
              </option>

              {yearOptions.map((item) => (
                <option
                  key={item}
                  value={item}
                >
                  {item}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Term">
            <select
              value={term}
              onChange={(event) => {
                setTerm(
                  event.target.value
                );
              }}
              style={fieldStyle}
              disabled={!year || loading}
            >
              <option value="">
                Select term
              </option>

              {termOptions.map((item) => (
                <option
                  key={item}
                  value={item}
                >
                  {item}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Enrolment Type">
            <select
              value={enrolmentTypeFilter}
              onChange={(event) => {
                setEnrolmentTypeFilter(
                  event.target.value
                );

                setSelectedCategory("all");
              }}
              style={fieldStyle}
              disabled={loading}
            >
              <option value="COE">
                COE
              </option>

              <option value="VOE">
                VOE
              </option>
            </select>
          </Field>

          <Field label="Attendance Period">
            <select
              value={selectedWeek}
              onChange={(event) => {
                setSelectedWeek(
                  event.target.value
                );
              }}
              style={fieldStyle}
              disabled={!term || loading}
            >
              <option value="">
                Select attendance period
              </option>

              {WEEK_NUMBERS.map(
                (weekNumber) => (
                  <option
                    key={weekNumber}
                    value={weekNumber}
                  >
                    {weekNumber === 1
                      ? "Week 1"
                      : `Week ${weekNumber}`}
                  </option>
                )
              )}
            </select>
          </Field>
        </div>
      </div>

      {error && (
        <div style={errorStyle}>
          {error}
        </div>
      )}

      {showResults && (
        <>
          {enrolmentFilteredStudentRows.length === 0 ? (
            <div style={emptyStyle}>
              No {enrolmentTypeFilter} attendance records were
              found for {campusFilter} · {year} · {term}.
            </div>
          ) : (
            <>
              <div style={resultHeadingStyle}>
                <div>
                  <strong>
                    {campusFilter} · {enrolmentTypeFilter} · All Courses · {year} · {term} ·{" "}
                    {selectedWeekNumber === 1
                      ? "Week 1"
                      : `Up to Week ${selectedWeekNumber}`}
                  </strong>

                  <div style={resultSubtextStyle}>
                    {enrolmentFilteredStudentRows.length} student
                    record
                    {enrolmentFilteredStudentRows.length === 1
                      ? ""
                      : "s"}{" "}
                    found
                  </div>
                </div>

                {selectedCategory !==
                  "all" && (
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedCategory(
                        "all"
                      )
                    }
                    style={
                      secondaryButtonStyle
                    }
                  >
                    <Users size={14} />
                    Show all students
                  </button>
                )}
              </div>

              <div
                style={categoryGridStyle}
              >
                {ATTENDANCE_CATEGORIES.map(
                  (category) => {
                    const selected =
                      selectedCategory ===
                      category.id;

                    const categoryCount =
                      categoryCounts[
                        category.id
                      ];

                    const toggleCategory = () =>
                      setSelectedCategory(
                        selected
                          ? "all"
                          : category.id
                      );

                    return (
                      <div
                        key={category.id}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selected}
                        onClick={toggleCategory}
                        onKeyDown={(event) => {
                          if (
                            event.key === "Enter" ||
                            event.key === " "
                          ) {
                            event.preventDefault();
                            toggleCategory();
                          }
                        }}
                        style={categoryCardStyle(
                          category.id,
                          selected
                        )}
                      >
                        
                          <span
                            style={
                              categoryCardLabelStyle
                            }
                          >
                            {category.label}
                          </span>

                        <div
                          style={
                            categoryCardTopRowStyle
                          }
                        >

                        <span
                          style={
                            categoryCountStyle
                          }
                        >
                          {categoryCount}
                        </span>
                        <button
                            type="button"
                            title={`Download ${category.label} category`}
                            aria-label={`Download ${category.label} attendance category`}
                            disabled={
                              categoryCount === 0
                            }
                            onClick={(event) => {
                              event.stopPropagation();

                              downloadCategoryAttendance(
                                category
                              );
                            }}
                            style={categoryDownloadButtonStyle(
                              categoryCount === 0
                            )}
                          >
                            <Download size={16} color="black"/>
                          </button>
                          </div>

                        <span
                          style={
                            categoryDescriptionStyle
                          }
                        >
                          {
                            category.description
                          }
                        </span>
                      </div>
                    );
                  }
                )}
              </div>

              <div
                style={{
                  ...panelStyle,
                  padding: 0,
                  overflow: "hidden",
                }}
              >
                <div style={tableTopBarStyle}>
                  Showing{" "}
                  {
                    filteredStudentRows.length
                  }{" "}
                  of {enrolmentFilteredStudentRows.length} student
                  records
                  {selectedCategory !== "all"
                    ? ` in ${getCategoryLabel(
                        selectedCategory
                      )}`
                    : ""}
                </div>

                <div
                  style={{
                    overflowX: "auto",
                    maxHeight: "70vh",
                  }}
                >
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th
                          style={{
                            ...headerCellStyle,
                            minWidth: 100,
                          }}
                        >
                          Student ID
                        </th>

                        <th
                          style={{
                            ...headerCellStyle,
                            minWidth: 175,
                          }}
                        >
                          Student
                        </th>

                        <th
                          style={{
                            ...headerCellStyle,
                            minWidth: 210,
                          }}
                        >
                          Course
                        </th>

                        <th
                          style={{
                            ...headerCellStyle,
                            minWidth: 210,
                          }}
                        >
                          Contact
                        </th>

                        <th
                          style={{
                            ...headerCellStyle,
                            minWidth: 92,
                            textAlign: "center",
                            whiteSpace: "normal",
                          }}
                        >
                          Week {selectedWeekNumber}
                        </th>

                        <th
                          style={{
                            ...headerCellStyle,
                            minWidth: 105,
                            textAlign: "center",
                            whiteSpace: "normal",
                          }}
                        >
                          Up to Week{" "}
                          {selectedWeekNumber}
                        </th>

                        <th
                          style={{
                            ...headerCellStyle,
                            minWidth: 180,
                            textAlign: "center",
                            whiteSpace: "normal",
                          }}
                        >
                          Daily Reminder Sent

                          <div style={dailyHeaderGridStyle}>
                            {[1, 2, 3, 4, 5].map(
                              (dayNumber) => (
                                <span key={dayNumber}>
                                  D{dayNumber}
                                </span>
                              )
                            )}
                          </div>
                        </th>

                        <th
                          style={{
                            ...headerCellStyle,
                            minWidth: 92,
                            textAlign: "center",
                            whiteSpace: "normal",
                          }}
                        >
                          Weekly
                        </th>

                        <th
                          style={{
                            ...headerCellStyle,
                            minWidth: 235,
                            whiteSpace: "normal",
                          }}
                        >
                          Student Response
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredStudentRows.map(
                        (student) => {
                          const rowKey =
                            getReminderRowKey(
                              student,
                              selectedWeekNumber
                            );

                          const isSaving =
                            Boolean(
                              savingRows[rowKey]
                            );

                          const wasSaved =
                            Boolean(
                              savedRows[rowKey]
                            );

                          return (
                          <tr
                            key={`${student.timesheetId}-${student.studentKey}-${student.studentId}`}
                          >
                            <td
                              style={
                                bodyCellStyle
                              }
                            >
                              <strong>
                                {
                                  student.studentId
                                }
                              </strong>
                            </td>

                            <td
                              style={
                                bodyCellStyle
                              }
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 7,
                                  flexWrap: "wrap",
                                }}
                              >
                                <span>
                                  {student.studentName}
                                </span>

                              </div>
                            </td>

                            <td
                              style={
                                bodyCellStyle
                              }
                            >
                              <div style={courseNameStyle}>
                                {student.courseCode
                                  ? `${student.courseCode} - `
                                  : ""}
                                {
                                  student.courseName
                                }
                              </div>
                            </td>

                            <td
                              style={
                                bodyCellStyle
                              }
                            >
                              <div style={contactCellStyle}>
                                <span>
                                  {student.contactEmail ||
                                    "—"}
                                </span>

                                <span style={contactNumberStyle}>
                                  {student.contactNumber ||
                                    "—"}
                                </span>
                              </div>
                            </td>

                            <td
                              style={
                                totalCellStyle
                              }
                            >
                              {student.selectedWeekPercentage ==
                              null ? (
                                <span
                                  style={
                                    blankPercentageStyle
                                  }
                                >
                                  —
                                </span>
                              ) : (
                                <span
                                  style={attendancePercentageStyle(
                                    student.selectedWeekPercentage
                                  )}
                                >
                                  {
                                    student.selectedWeekPercentage
                                  }
                                  %
                                </span>
                              )}
                            </td>

                            <td
                              style={
                                totalCellStyle
                              }
                            >
                              <span
                                style={attendancePercentageStyle(
                                  student.upToDatePercentage
                                )}
                              >
                                {
                                  student.upToDatePercentage
                                }
                                %
                              </span>
                            </td>

                            <td style={reminderCellStyle}>
                              <div style={dailyReminderGridStyle}>
                                {Array.from(
                                  { length: 5 },
                                  (_, index) => {
                                    const dayNumber =
                                      index + 1;

                                    const dayKey =
                                      `day${dayNumber}`;

                                    const disabled =
                                      dayNumber >
                                      Number(
                                        student.attendanceDaysPerWeek ||
                                        3
                                      );

                                    return (
                                      <label
                                        key={dayKey}
                                        title={
                                          disabled
                                            ? `Not used for this ${student.attendanceDaysPerWeek}-day schedule`
                                            : `Day ${dayNumber} reminder sent`
                                        }
                                        style={dailyReminderItemStyle(
                                          disabled
                                        )}
                                      >
                                        {disabled ? (
                                          <span style={inactiveDayStyle}>
                                            —
                                          </span>
                                        ) : (
                                          <input
                                            type="checkbox"
                                            checked={
                                              Boolean(
                                                student.dailyReminders?.[
                                                  dayKey
                                                ]
                                              )
                                            }
                                            disabled={isSaving}
                                            onChange={(event) =>
                                              changeDailyReminder(
                                                student,
                                                dayKey,
                                                event.target.checked
                                              )
                                            }
                                            style={compactCheckboxStyle}
                                          />
                                        )}
                                      </label>
                                    );
                                  }
                                )}
                              </div>
                            </td>

                            <td style={totalCellStyle}>
                              <input
                                type="checkbox"
                                title="Weekly reminder sent"
                                checked={
                                  Boolean(
                                    student.weeklyReminderSent
                                  )
                                }
                                disabled={isSaving}
                                onChange={(event) =>
                                  changeWeeklyReminder(
                                    student,
                                    event.target.checked
                                  )
                                }
                                style={weeklyCheckboxStyle}
                              />
                            </td>

                            <td style={bodyCellStyle}>
                              <input
                                type="text"
                                value={
                                  student.studentResponse ||
                                  ""
                                }
                                placeholder="Enter response"
                                disabled={isSaving}
                                onChange={(event) =>
                                  changeStudentResponse(
                                    student,
                                    event.target.value
                                  )
                                }
                                onBlur={() =>
                                  saveStudentResponse(
                                    student
                                  )
                                }
                                onKeyDown={(event) => {
                                  if (
                                    event.key === "Enter"
                                  ) {
                                    event.currentTarget.blur();
                                  }
                                }}
                                style={studentResponseStyle}
                              />

                              {(isSaving || wasSaved) && (
                                <div
                                  style={{
                                    ...rowSaveStatusStyle,
                                    color: isSaving
                                      ? "#64748b"
                                      : "#15803d",
                                  }}
                                >
                                  {isSaving
                                    ? "Saving…"
                                    : "Saved"}
                                </div>
                              )}
                            </td>

                          </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function Field({
  label,
  children,
}) {
  return (
    <div>
      <label style={labelStyle}>
        {label}
      </label>

      {children}
    </div>
  );
}

function attendancePercentageStyle(
  percentage
) {
  const category =
    getAttendanceCategory(percentage);

  const styles = {
    zero: {
      background: "#fef2f2",
      color: "#991b1b",
      border: "#fecaca",
    },

    under50: {
      background: "#fff1f2",
      color: "#be123c",
      border: "#fecdd3",
    },

    "50to80": {
      background: "#fff7ed",
      color: "#9a3412",
      border: "#fed7aa",
    },

    "80to100": {
      background: "#f0fdf4",
      color: "#166534",
      border: "#bbf7d0",
    },
  };

  const selected =
    styles[category];

  return {
    display: "inline-block",
    minWidth: 62,
    padding: "4px 9px",
    borderRadius: 20,
    fontWeight: 800,
    background: selected.background,
    color: selected.color,
    border: `1px solid ${selected.border}`,
  };
}

function categoryCardStyle(
  categoryId,
  selected
) {
  const accents = {
    zero: {
      border: "#fecaca",
      background: "#fef2f2",
    },

    under50: {
      border: "#fecdd3",
      background: "#fff1f2",
    },

    "50to80": {
      border: "#fed7aa",
      background: "#fff7ed",
    },

    "80to100": {
      border: "#bbf7d0",
      background: "#f0fdf4",
    },
  };

  const accent =
    accents[categoryId];

  return {
    width: "100%",
    display: "grid",
    gap: 3,
    textAlign: "left",
    padding: "12px 13px",
    borderRadius: 9,
    border: selected
      ? "2px solid #056ec4"
      : `1px solid ${accent.border}`,
    background: accent.background,
    cursor: "pointer",
    boxSizing: "border-box",
  };
}

const headingRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
  flexWrap: "wrap",
  marginBottom: 14,
};

const headingStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const titleStyle = {
  margin: 0,
  color: "#1e293b",
  fontSize: "1.05rem",
};

const subtitleStyle = {
  maxWidth: 900,
  margin: "5px 0 0",
  color: "#64748b",
  fontSize: "0.82rem",
  lineHeight: 1.5,
};

const panelStyle = {
  padding: 14,
  marginBottom: 14,
  border: "1px solid #dbe3ec",
  borderRadius: 10,
  background: "#fff",
};

const filterGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit, minmax(200px, 1fr))",

  gap: 12,
  alignItems: "end",
};

const fieldStyle = {
  width: "100%",
  minHeight: 39,
  padding: "8px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 7,
  background: "#fff",
  color: "#1e293b",
  fontSize: "0.82rem",
};

const labelStyle = {
  display: "block",
  marginBottom: 5,
  color: "#475569",
  fontWeight: 700,
  fontSize: "0.76rem",
};

const primaryButtonStyle = {
  minHeight: 39,
  display: "inline-flex",
  justifyContent: "center",
  alignItems: "center",
  gap: 6,
  padding: "8px 12px",
  border: "1px solid #056ec4",
  borderRadius: 7,
  background: "#056ec4",
  color: "#fff",
  cursor: "pointer",
  fontSize: "0.82rem",
  fontWeight: 700,
};

const secondaryButtonStyle = {
  minHeight: 36,
  display: "inline-flex",
  justifyContent: "center",
  alignItems: "center",
  gap: 6,
  padding: "7px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 7,
  background: "#fff",
  color: "#334155",
  cursor: "pointer",
  fontSize: "0.79rem",
  fontWeight: 700,
};

const resultHeadingStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
  marginBottom: 10,
  color: "#334155",
};

const resultSubtextStyle = {
  marginTop: 3,
  color: "#64748b",
  fontSize: "0.76rem",
};

const categoryGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit, minmax(170px, 1fr))",

  gap: 9,
  marginBottom: 14,
};

const categoryCardTopRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
};

const categoryCardLabelStyle = {
  color: "#334155",
  fontSize: "0.76rem",
  fontWeight: 800,
};

function categoryDownloadButtonStyle(
  disabled
) {
  return {
    width: 30,
    height: 30,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    border: "1px solid #f8fafc",
    borderRadius: 7,
    background: disabled
      ? "#f8fafc"
      : "#fff",
    color: disabled
      ? "#cbd5e1"
      : "#056ec4",
    cursor: disabled
      ? "not-allowed"
      : "pointer",
    flexShrink: 0,
  };
}

const categoryCountStyle = {
  color: "#0f172a",
  fontSize: "1.35rem",
  fontWeight: 800,
};

const categoryDescriptionStyle = {
  color: "#64748b",
  fontSize: "0.68rem",
};

const tableTopBarStyle = {
  padding: "10px 12px",
  borderBottom: "1px solid #e2e8f0",
  background: "#f8fafc",
  color: "#475569",
  fontSize: "0.78rem",
  fontWeight: 700,
};

const tableStyle = {
  width: "100%",
  minWidth: 1430,
  borderCollapse: "collapse",
  fontSize: "0.76rem",
};

const headerCellStyle = {
  padding: "9px 8px",
  borderRight: "1px solid #cbd5e1",
  borderBottom: "1px solid #cbd5e1",
  background: "#f8fafc",
  color: "#334155",
  textAlign: "left",
  whiteSpace: "nowrap",
  fontWeight: 700,
  position: "sticky",
  top: 0,
  zIndex: 2,
};

const bodyCellStyle = {
  padding: 8,
  borderRight: "1px solid #e2e8f0",
  borderBottom: "1px solid #e2e8f0",
  verticalAlign: "middle",
  color: "#1e293b",
  background: "#fff",
};

const totalCellStyle = {
  ...bodyCellStyle,
  textAlign: "center",
  fontWeight: 700,
};

const blankPercentageStyle = {
  color: "#94a3b8",
  fontWeight: 700,
};

const errorStyle = {
  marginBottom: 12,
  padding: "10px 12px",
  border: "1px solid #fca5a5",
  borderRadius: 8,
  background: "#fef2f2",
  color: "#b91c1c",
  fontSize: "0.82rem",
};

const emptyStyle = {
  padding: 35,
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  background: "#fff",
  textAlign: "center",
  color: "#64748b",
  fontSize: "0.85rem",
};
function enrolmentBadgeStyle(
  enrolmentType
) {
  const isVOE =
    enrolmentType === "VOE";

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 40,
    padding: "2px 7px",
    borderRadius: 20,
    background: isVOE
      ? "#fff7ed"
      : "#eff6ff",
    color: isVOE
      ? "#c2410c"
      : "#1d4ed8",
    border: `1px solid ${
      isVOE
        ? "#fed7aa"
        : "#bfdbfe"
    }`,
    fontSize: "0.66rem",
    fontWeight: 800,
  };
}

const courseNameStyle = {
  maxWidth: 210,
  fontWeight: 700,
  lineHeight: 1.35,
  whiteSpace: "normal",
};

const contactCellStyle = {
  display: "grid",
  gap: 3,
  maxWidth: 210,
  overflowWrap: "anywhere",
  lineHeight: 1.3,
};

const contactNumberStyle = {
  color: "#64748b",
};

const scheduleBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 48,
  padding: "2px 7px",
  borderRadius: 20,
  background: "#f1f5f9",
  color: "#475569",
  border: "1px solid #cbd5e1",
  fontSize: "0.64rem",
  fontWeight: 800,
};

const dailyHeaderGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(5, 1fr)",
  gap: 4,
  marginTop: 5,
  color: "#64748b",
  fontSize: "0.61rem",
  fontWeight: 700,
};

const reminderCellStyle = {
  ...bodyCellStyle,
  padding: "8px 10px",
  textAlign: "center",
};

const dailyReminderGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(5, 1fr)",
  gap: 4,
  alignItems: "center",
};

function dailyReminderItemStyle(
  disabled
) {
  return {
    width: 28,
    height: 28,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    justifySelf: "center",
    border: `1px solid ${
      disabled
        ? "#e2e8f0"
        : "#cbd5e1"
    }`,
    borderRadius: 5,
    background: disabled
      ? "#f8fafc"
      : "#fff",
    cursor: disabled
      ? "default"
      : "pointer",
  };
}

const compactCheckboxStyle = {
  width: 16,
  height: 16,
  margin: 0,
  cursor: "pointer",
};

const inactiveDayStyle = {
  color: "#cbd5e1",
  fontSize: "0.8rem",
  lineHeight: 1,
};

const weeklyCheckboxStyle = {
  width: 18,
  height: 18,
  margin: 0,
  cursor: "pointer",
};

const studentResponseStyle = {
  width: "100%",
  minWidth: 210,
  minHeight: 34,
  padding: "7px 8px",
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  background: "#fff",
  color: "#1e293b",
  fontFamily: "inherit",
  fontSize: "0.74rem",
  boxSizing: "border-box",
};

const rowSaveStatusStyle = {
  minHeight: 14,
  marginTop: 3,
  fontSize: "0.62rem",
  fontWeight: 700,
};